// Annual OKR — coach REST surface.
//
// Source of truth: `docs/annual-okr/20-fe-integration-guide.md` §6, the verbatim
// backend handoff. Paths are quoted from it; if this file and that doc disagree,
// the doc wins.
//
// Placeholders are `{uniqueId}` / `{annualObjectiveId}` / `{commentId}`,
// matching `ExecutionBlueprintUrls.js`'s convention in this repo rather than the
// CEO app's arrow-function style.
//
// Only GET_DASHBOARD is called today. The rest are declared because the contract
// defines them and agreeing paths once beats rediscovering them per screen; it
// is one exported object, so nothing dangles.
export const annualOkrUrls = {
  // §6.1 — the coach's portfolio. No :uniqueId; lists across all their
  // companies, one card per client with `stage`, `stage_label`, `stage_index`,
  // `button {label, enabled}` and the full `timestamps[]` milestone timeline.
  GET_DASHBOARD: "/coach/annual-okr/dashboard",

  // §6.2 POST — SUBMITTED_FOR_COACH → COACH_REVIEW_IN_PROGRESS. Idempotent if
  // already there; 409 from any other state.
  POST_REVIEW_ENTER: "/coach/annual-okr/{uniqueId}/review/enter",

  // §6.3 GET — every CONFIRMED Objective with its review status and comments,
  // plus the counts the review header shows.
  GET_REVIEW: "/coach/annual-okr/{uniqueId}/review",

  // §6.4 PUT — body `{ status: "accepted" | "to_discuss" }`. Freely re-settable
  // (upsert), unlike Live Review's one-directional discuss-marking.
  PUT_OBJECTIVE_STATUS:
    "/coach/annual-okr/{uniqueId}/review/objectives/{annualObjectiveId}/status",

  // §6.5 — `scope_key` is the Objective id as a STRING and is required when
  // `scope_type: "objective"`.
  //
  // ⚠️ There is no PUT/edit route for Annual OKR comments, unlike Strategy
  // Canvas's. Delete and re-add instead — and `CommentItem` already supports
  // that: omit `onEdit` to get delete without edit.
  POST_COMMENT: "/coach/annual-okr/{uniqueId}/review/comments",
  DELETE_COMMENT: "/coach/annual-okr/{uniqueId}/review/comments/{commentId}",

  // §6.9 GET — THE COACH'S TRACKING READ, and the route the post-lock board
  // uses. Shipped 2026-09-22 in direct response to the two problems below;
  // returns Objectives + Key Results + progress in the same shape as the CEO's
  // §4.6, including `progress_status`, `owner_name` and each KR's
  // `progress_percent`, plus `meeting_datetime` / `duration_minutes`.
  //
  // ⚠️ TWO WRONG ROUTES PRECEDED THIS ONE. The history is worth keeping because
  // both mistakes are easy to repeat:
  //
  //   1. The CEO's §4.6 `/annual-okr/{uniqueId}/tracking` — 500'd. §4.6 says
  //      "view-only for everyone, no CEOGuard", which reads like an invitation
  //      and is not one: §3's auth table lists it as still behind
  //      `UserAuthGuard` with a REQUIRED `company_id` header. The coach app
  //      sends a coach token and has no `company_id`, so the guard couldn't
  //      resolve an identity and the service queried on `undefined`. "No
  //      CEOGuard" means any USER of that company may read it, not any caller.
  //   2. §6.6 `okr-mode` — loaded fine, but is a narrower payload: no
  //      `progress_status`, no `owner_name`. Every row on the coach's board
  //      read "Not Started" whatever the CEO had set, and owners showed as
  //      "User #44".
  //
  // Don't route the coach app at `/annual-okr/...` again, and don't fall back
  // to `okr-mode` for anything that needs progress or an owner name.
  GET_TRACKING: "/coach/annual-okr/{uniqueId}/tracking",

  // §6.6 GET — read-only Objectives with no review UI attached, for a mode
  // toggle. The analogue of EXW's Blueprint Mode. Not currently called: the
  // locked board uses GET_TRACKING above, which is a superset of this.
  GET_OKR_MODE: "/coach/annual-okr/{uniqueId}/okr-mode",

  // §6.7 POST — body `{ note_to_ceo?, scheduled_date?, scheduled_time?,
  // duration_minutes? }`. Gate: every CONFIRMED Objective needs a review status
  // or this 409s with the outstanding count. Lands on LIVE_REVIEW_SCHEDULED if a
  // meeting was scheduled, COACH_REVIEW_COMPLETED if not.
  POST_REVIEW_SUBMIT: "/coach/annual-okr/{uniqueId}/review/submit",

  // §6.8 POST — COACH_REVIEW_COMPLETED or LIVE_REVIEW_SCHEDULED →
  // LIVE_REVIEW_IN_PROGRESS. Also best-effort pushes to the CEO's personal
  // socket room so their app can offer a Join prompt without polling.
  POST_LIVE_REVIEW_START: "/coach/annual-okr/{uniqueId}/live-review/start",
};

// ─── Stage values (§6.1) ────────────────────────────────────────────────────
// Deliberately NOT declared yet. The nine values are NOT_STARTED,
// SETUP_STARTED, INTERVIEW_IN_PROGRESS, SENT_TO_COACH, COACH_REVIEW_STARTED,
// COACH_REVIEW_COMPLETED, LIVE_REVIEW_SCHEDULED, LIVE_REVIEW_STARTED and
// LOCKED — nine, where `timestamps[]` is an 8-milestone timeline, because
// NOT_STARTED is computed (no run row) and is never a milestone.
//
// The dashboard card branches on nothing: the server sends `stage_label` and
// `button { label, enabled }`, so the copy and the enabled state are its
// decisions, not ours. An enum here would be a dead export until a screen
// genuinely routes by stage — it comes back then, written from §6.1.
